import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SafeImage } from '@/components/ui/safe-image';
import { Eye, EyeOff, Images, Loader2, MapPin, X } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { StoreProfileDetail } from '../../types';
import { ProvenanceBadge } from '../ProvenanceBadge';

interface StoreImageryTabProps {
  profile: StoreProfileDetail;
  storeId?: number;
  storeIdParam?: string;
}

type SourceFilter = 'all' | 'campaign_proof' | 'baseline';
type MappingFilter = 'all' | 'mapped' | 'unmapped' | 'suggestion';
type DateFilter = 'all' | 'last_30' | 'last_90' | 'older';

interface ImageryItem {
  id: string;
  kind: 'gallery' | 'reference';
  url: string;
  caption: string;
  captureDate: string;
  campaignName: string | null;
  floorplanZoneId: string | null;
  floorplanAisleId: string | null;
  instoreLocationIds: number[];
  hidden: boolean;
  capturedBy: string;
  /** liveId for reference images (numeric server id) */
  liveId: number | null;
}

function monthKey(date: string): string {
  return date.slice(0, 7) || 'Unknown date';
}

function monthLabel(key: string): string {
  if (key === 'Unknown date') return key;
  const [year, month] = key.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
}

function daysSince(date: string): number {
  const parsed = Date.parse(date);
  if (Number.isNaN(parsed)) return Number.POSITIVE_INFINITY;
  return (Date.now() - parsed) / 86_400_000;
}

/**
 * Store imagery — one surface for campaign proof (posting evidence gallery) and
 * baseline store imagery (reference images). Chronological groups, real filters,
 * and bulk zone assignment for gallery photos.
 */
export function StoreImageryTab({ profile, storeId, storeIdParam }: StoreImageryTabProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canEdit = storeId != null && storeIdParam != null;

  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [mappingFilter, setMappingFilter] = useState<MappingFilter>('all');
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [showHidden, setShowHidden] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkZoneId, setBulkZoneId] = useState('');
  const [pendingBulk, setPendingBulk] = useState(false);
  const [pendingImageId, setPendingImageId] = useState<string | null>(null);
  const [expandedImage, setExpandedImage] = useState<ImageryItem | null>(null);

  const items = useMemo<ImageryItem[]>(() => {
    const gallery: ImageryItem[] = profile.images.map((image) => {
      const campaignTag = image.tags.find(
        (tag) => !['posting', 'gallery'].includes(tag.toLowerCase()),
      );
      return {
        id: image.id,
        kind: 'gallery' as const,
        url: image.url,
        caption: image.caption,
        captureDate: image.captureDate,
        campaignName: campaignTag ?? null,
        floorplanZoneId: image.floorplanZoneId ?? profile.floorplanImageZoneTags?.[image.id] ?? null,
        floorplanAisleId: image.floorplanAisleId ?? null,
        instoreLocationIds: image.instoreLocationIds ?? [],
        hidden: image.hidden ?? false,
        capturedBy: image.capturedBy ?? 'Field operator',
        liveId: null,
      };
    });
    const reference: ImageryItem[] = profile.referenceImages.map((image) => ({
      id: `live-refimg-${image.id}`,
      kind: 'reference' as const,
      url: image.thumbnailUrl ?? image.imageUrl,
      caption: image.title,
      captureDate: image.captureDate ?? '',
      campaignName: null,
      floorplanZoneId: image.floorplanZoneId,
      floorplanAisleId: image.floorplanAisleId,
      instoreLocationIds: image.instoreLocationId != null ? [image.instoreLocationId] : [],
      hidden: false,
      capturedBy: image.uploadedByName ?? '—',
      liveId: image.id,
    }));
    return [...gallery, ...reference].sort((a, b) => b.captureDate.localeCompare(a.captureDate));
  }, [profile.images, profile.referenceImages, profile.floorplanImageZoneTags]);

  const campaigns = useMemo(
    () => [...new Set(items.map((item) => item.campaignName).filter((name): name is string => Boolean(name)))].sort(),
    [items],
  );

  const hiddenCount = items.filter((item) => item.hidden).length;
  const unmappedCount = items.filter(
    (item) => !item.floorplanZoneId && !item.floorplanAisleId && item.instoreLocationIds.length === 0,
  ).length;

  const filtered = items.filter((item) => {
    if (!showHidden && item.hidden) return false;
    if (sourceFilter === 'campaign_proof' && item.kind !== 'gallery') return false;
    if (sourceFilter === 'baseline' && item.kind !== 'reference') return false;
    if (campaignFilter !== 'all' && item.campaignName !== campaignFilter) return false;
    if (mappingFilter === 'mapped' && !item.floorplanZoneId && !item.floorplanAisleId) return false;
    if (mappingFilter === 'unmapped' && (item.floorplanZoneId || item.floorplanAisleId || item.instoreLocationIds.length > 0)) return false;
    if (mappingFilter === 'suggestion' && (item.floorplanZoneId || item.floorplanAisleId || item.instoreLocationIds.length === 0)) return false;
    if (locationFilter.startsWith('zone:') && item.floorplanZoneId !== locationFilter.slice(5)) return false;
    if (locationFilter.startsWith('aisle:') && item.floorplanAisleId !== locationFilter.slice(6)) return false;
    if (dateFilter === 'last_30' && daysSince(item.captureDate) > 30) return false;
    if (dateFilter === 'last_90' && daysSince(item.captureDate) > 90) return false;
    if (dateFilter === 'older' && daysSince(item.captureDate) <= 90) return false;
    return true;
  });

  const groups = useMemo(() => {
    const byMonth = new Map<string, ImageryItem[]>();
    for (const item of filtered) {
      const key = monthKey(item.captureDate);
      const list = byMonth.get(key) ?? [];
      list.push(item);
      byMonth.set(key, list);
    }
    return [...byMonth.entries()].map(([key, groupItems]) => ({
      key,
      label: monthLabel(key),
      items: groupItems,
    }));
  }, [filtered]);

  const toggleSelect = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/store-profiles/summary'] }),
    ]);
  };

  const assignZoneToItem = async (item: ImageryItem, zoneId: string | null): Promise<void> => {
    if (storeId == null) return;
    if (item.kind === 'gallery') {
      const res = await apiRequest(
        `/api/store-profiles/${storeId}/floorplan/image-tags`,
        'PATCH',
        { imageId: item.id, zoneId },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to tag photo');
      }
      return;
    }
    if (item.liveId == null) return;
    const res = await apiRequest(
      `/api/store-profiles/${storeId}/reference-images/${item.liveId}`,
      'PATCH',
      { floorplanZoneId: zoneId },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? 'Failed to tag image');
    }
  };

  const bulkAssign = async () => {
    if (!bulkZoneId || selectedIds.size === 0 || pendingBulk) return;
    setPendingBulk(true);
    const selected = items.filter((item) => selectedIds.has(item.id));
    let succeeded = 0;
    let failed = 0;
    try {
      for (const item of selected) {
        try {
          await assignZoneToItem(item, bulkZoneId);
          succeeded += 1;
        } catch {
          failed += 1;
        }
      }
      await invalidate();
      if (failed === 0) {
        toast({ title: `${succeeded} photo${succeeded === 1 ? '' : 's'} assigned to zone` });
      } else {
        toast({
          title: `Assigned ${succeeded}, ${failed} failed`,
          description: 'Failed items stayed unassigned — retry them individually.',
          variant: 'destructive',
        });
      }
      setSelectedIds(new Set());
      setSelectionMode(false);
    } finally {
      setPendingBulk(false);
    }
  };

  const toggleHidden = async (item: ImageryItem) => {
    if (!canEdit || item.kind !== 'gallery') return;
    setPendingImageId(item.id);
    try {
      const res = await apiRequest(
        `/api/store-profiles/${storeId}/floorplan/gallery-image-meta`,
        'PATCH',
        {
          imageId: item.id,
          hidden: !item.hidden,
          instoreLocationIds: item.instoreLocationIds,
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to update photo');
      }
      await invalidate();
      toast({ title: item.hidden ? 'Photo unhidden' : 'Photo hidden' });
    } catch (error) {
      toast({
        title: 'Could not update photo',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setPendingImageId(null);
    }
  };

  const selectedZoneLabel = (zoneId: string | null) =>
    zoneId ? profile.floorplanZones.find((zone) => zone.id === zoneId)?.label ?? null : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={sourceFilter} onValueChange={(value) => setSourceFilter(value as SourceFilter)}>
          <SelectTrigger className="w-48" aria-label="Filter by source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All imagery</SelectItem>
            <SelectItem value="campaign_proof">Campaign proof</SelectItem>
            <SelectItem value="baseline">Baseline store imagery</SelectItem>
          </SelectContent>
        </Select>
        <Select value={campaignFilter} onValueChange={setCampaignFilter}>
          <SelectTrigger className="w-52" aria-label="Filter by campaign"><SelectValue placeholder="Campaign" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All campaigns</SelectItem>
            {campaigns.map((campaign) => (
              <SelectItem key={campaign} value={campaign}>{campaign}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={locationFilter} onValueChange={setLocationFilter}>
          <SelectTrigger className="w-48" aria-label="Filter by aisle or zone"><SelectValue placeholder="Aisle / zone" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any location</SelectItem>
            {profile.aisles.map((aisle) => (
              <SelectItem key={aisle.id} value={`aisle:${aisle.id}`}>Aisle {aisle.number} — {aisle.name}</SelectItem>
            ))}
            {profile.floorplanZones.map((zone) => (
              <SelectItem key={zone.id} value={`zone:${zone.id}`}>Zone: {zone.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={mappingFilter} onValueChange={(value) => setMappingFilter(value as MappingFilter)}>
          <SelectTrigger className="w-44" aria-label="Filter by mapping status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any mapping</SelectItem>
            <SelectItem value="mapped">Mapped</SelectItem>
            <SelectItem value="suggestion">Suggestion pending</SelectItem>
            <SelectItem value="unmapped">Unmapped</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dateFilter} onValueChange={(value) => setDateFilter(value as DateFilter)}>
          <SelectTrigger className="w-40" aria-label="Filter by date"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any date</SelectItem>
            <SelectItem value="last_30">Last 30 days</SelectItem>
            <SelectItem value="last_90">Last 90 days</SelectItem>
            <SelectItem value="older">Older than 90 days</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1.5 text-sm cursor-pointer ml-auto">
          <Checkbox
            checked={showHidden}
            onCheckedChange={(checked) => setShowHidden(checked === true)}
            aria-label="Show hidden photos"
          />
          Show hidden{hiddenCount > 0 ? ` (${hiddenCount})` : ''}
        </label>
        {canEdit && (
          <Button
            type="button"
            variant={selectionMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setSelectionMode((current) => !current);
              setSelectedIds(new Set());
            }}
          >
            {selectionMode ? 'Done selecting' : 'Select photos'}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
        <ProvenanceBadge provenance={items.length > 0 ? 'live' : 'missing'} />
        <span>
          {filtered.length} of {items.length} photos
          {unmappedCount > 0 && ` · ${unmappedCount} unmapped`}
          {hiddenCount > 0 && !showHidden && ` · ${hiddenCount} hidden (excluded)`}
        </span>
      </div>

      {selectionMode && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-3 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium">{selectedIds.size} selected</span>
            <Select value={bulkZoneId} onValueChange={setBulkZoneId}>
              <SelectTrigger className="w-56 h-8" aria-label="Bulk assign to zone"><SelectValue placeholder="Assign all to zone…" /></SelectTrigger>
              <SelectContent>
                {profile.floorplanZones.map((zone) => (
                  <SelectItem key={zone.id} value={zone.id}>{zone.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              disabled={!bulkZoneId || selectedIds.size === 0 || pendingBulk}
              onClick={() => void bulkAssign()}
            >
              {pendingBulk && <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden />}
              Assign {selectedIds.size > 0 ? selectedIds.size : ''} to zone
            </Button>
            {profile.floorplanZones.length === 0 && (
              <span className="text-xs text-gray-500">Define floorplan zones first.</span>
            )}
          </CardContent>
        </Card>
      )}

      {groups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <Images className="h-10 w-10 mx-auto mb-2 opacity-30" aria-hidden />
            {items.length === 0 ? 'No imagery for this store yet.' : 'No photos match these filters.'}
          </CardContent>
        </Card>
      ) : (
        groups.map((group) => (
          <section key={group.key} aria-label={group.label}>
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              {group.label}
              <Badge variant="secondary">{group.items.length}</Badge>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {group.items.map((item) => {
                const zoneLabel = selectedZoneLabel(item.floorplanZoneId);
                const isSelected = selectedIds.has(item.id);
                return (
                  <figure
                    key={item.id}
                    className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                      isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent'
                    } ${item.hidden ? 'opacity-60' : ''}`}
                  >
                    <button
                      type="button"
                      className="block w-full aspect-[4/3] relative"
                      onClick={() => (selectionMode ? toggleSelect(item.id) : setExpandedImage(item))}
                      aria-label={`${item.caption} — ${item.captureDate}`}
                      aria-pressed={selectionMode ? isSelected : undefined}
                    >
                      <SafeImage src={item.url} alt={item.caption} className="w-full h-full object-cover" placeholderType="photo" />
                      <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 to-transparent p-1.5 text-left">
                        <span className="block text-white text-[11px] truncate">{item.caption}</span>
                        <span className="block text-white/75 text-[10px]">{item.captureDate}</span>
                      </span>
                    </button>
                    <span className="absolute top-1.5 left-1.5 flex flex-col gap-1">
                      <Badge
                        variant="outline"
                        className={`text-[9px] px-1 py-0 ${
                          item.kind === 'gallery'
                            ? 'bg-green-600/85 text-white border-green-500/60'
                            : 'bg-sky-600/85 text-white border-sky-500/60'
                        }`}
                      >
                        {item.kind === 'gallery' ? 'Campaign proof' : 'Baseline'}
                      </Badge>
                      {item.hidden && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 bg-slate-900/80 text-white border-slate-600">
                          Hidden
                        </Badge>
                      )}
                    </span>
                    {zoneLabel && (
                      <span className="absolute top-1.5 right-1.5">
                        <Badge variant="outline" className="text-[9px] px-1 py-0 bg-black/60 text-white border-white/30">
                          <MapPin className="h-2.5 w-2.5 mr-0.5" aria-hidden />{zoneLabel}
                        </Badge>
                      </span>
                    )}
                    {selectionMode && (
                      <span className="absolute bottom-1.5 right-1.5">
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(item.id)} aria-label={`Select ${item.caption}`} className="bg-white/90" />
                      </span>
                    )}
                  </figure>
                );
              })}
            </div>
          </section>
        ))
      )}

      {expandedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={expandedImage.caption}
          onClick={() => setExpandedImage(null)}
        >
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-4" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-2 mb-3">
              <div>
                <p className="font-medium">{expandedImage.caption}</p>
                <p className="text-sm text-gray-500">
                  {expandedImage.captureDate} · {expandedImage.capturedBy} · {expandedImage.kind === 'gallery' ? 'Campaign proof' : 'Baseline store imagery'}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                {canEdit && expandedImage.kind === 'gallery' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={pendingImageId === expandedImage.id}
                    onClick={() => {
                      void toggleHidden(expandedImage);
                      setExpandedImage(null);
                    }}
                  >
                    {pendingImageId === expandedImage.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    ) : expandedImage.hidden ? (
                      <><Eye className="h-4 w-4 mr-1" aria-hidden />Unhide</>
                    ) : (
                      <><EyeOff className="h-4 w-4 mr-1" aria-hidden />Hide</>
                    )}
                  </Button>
                )}
                <Button type="button" variant="ghost" size="icon" onClick={() => setExpandedImage(null)} aria-label="Close preview">
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            </div>
            <SafeImage
              src={expandedImage.url}
              alt={expandedImage.caption}
              className="w-full rounded-lg object-contain max-h-[60vh] bg-slate-50"
              placeholderType="photo"
            />
            {selectedZoneLabel(expandedImage.floorplanZoneId) && (
              <p className="text-sm text-gray-600 mt-2 flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" aria-hidden />Zone: {selectedZoneLabel(expandedImage.floorplanZoneId)}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
